import React from "react";
import { cn } from "@/lib/utils";
import { ArrowRight, TrendingDown, TrendingUp } from "lucide-react";
import type { LucideIcon } from "lucide-react";

type Trend = "up" | "down" | "neutral";

interface KpiDelta {
  value: number;
  label?: string;
  trend?: Trend;
}

interface KpiEmpty {
  title: string;
  description?: string;
  actionLabel: string;
  onAction?: () => void;
}

interface KpiCardProps {
  label: string;
  value?: React.ReactNode;
  icon?: LucideIcon;
  delta?: KpiDelta;
  helper?: string;
  loading?: boolean;
  empty?: KpiEmpty;
  sparkline?: React.ReactNode;
  className?: string;
}

const trendColor: Record<Trend, string> = {
  up: "text-app-success",
  down: "text-app-danger",
  neutral: "text-app-fg-muted",
};

export function KpiCard({
  label,
  value,
  icon: Icon,
  delta,
  helper,
  loading = false,
  empty,
  sparkline,
  className,
}: KpiCardProps) {
  const showEmpty =
    !loading && Boolean(empty) && (value === undefined || value === null);

  return (
    <div
      className={cn(
        "flex min-h-[140px] flex-col justify-between rounded-xl border border-app-border bg-app-surface p-6 shadow-premium-sm transition-shadow duration-200 hover:shadow-premium-md",
        className
      )}
    >
      <header className="flex items-start justify-between">
        <span className="text-sm font-medium leading-none text-app-fg-muted">
          {label}
        </span>
        {Icon && (
          <Icon className="size-4 text-app-fg-subtle" strokeWidth={1.75} />
        )}
      </header>

      <div className="mt-6 space-y-2">
        {loading && (
          <div className="h-9 w-32 animate-pulse rounded-md bg-app-surface-muted" />
        )}

        {!loading && showEmpty && empty && (
          <div className="space-y-1.5">
            <p className="text-base font-medium text-app-fg-strong">
              {empty.title}
            </p>
            {empty.description && (
              <p className="text-sm text-app-fg-muted">{empty.description}</p>
            )}
            <button
              type="button"
              onClick={empty.onAction}
              className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-brand transition-colors hover:text-brand-hover"
            >
              {empty.actionLabel}
              <ArrowRight className="size-3.5" strokeWidth={2} />
            </button>
          </div>
        )}

        {!loading && !showEmpty && (
          <>
            <h3
              className="text-[2.5rem] font-medium leading-[1.02] tracking-[-0.025em] text-app-fg-strong"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {value}
            </h3>

            {(delta || helper) && (
              <div className="flex items-center gap-2 text-sm">
                {delta && (
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 font-medium",
                      trendColor[delta.trend ?? "neutral"]
                    )}
                  >
                    {delta.trend === "up" && (
                      <TrendingUp className="size-3.5" strokeWidth={2} />
                    )}
                    {delta.trend === "down" && (
                      <TrendingDown className="size-3.5" strokeWidth={2} />
                    )}
                    {delta.value > 0 ? "+" : ""}
                    {delta.value}%
                  </span>
                )}
                {delta?.label && (
                  <span className="text-app-fg-muted">{delta.label}</span>
                )}
                {!delta && helper && (
                  <span className="text-app-fg-muted">{helper}</span>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {sparkline && !showEmpty && !loading && (
        <div className="-mx-1 mt-4">{sparkline}</div>
      )}
    </div>
  );
}

KpiCard.displayName = "KpiCard";
